import { useState, useCallback, useMemo } from 'react';

export interface BulkSelectionState<T> {
  selectedIds: Set<number>;
  isAllSelected: boolean;
  isSomeSelected: boolean;
  selectAll: (items: T[], getIdFn: (item: T) => number) => void;
  deselectAll: () => void;
  toggleItem: (id: number) => void;
  toggleAll: (items: T[], getIdFn: (item: T) => number) => void;
  isSelected: (id: number) => boolean;
  selectedCount: number;
  getSelectedItems: (items: T[], getIdFn: (item: T) => number) => T[];
}

export function useBulkSelection<T>(): BulkSelectionState<T> {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const selectAll = useCallback((items: T[], getIdFn: (item: T) => number) => {
    setSelectedIds(new Set(items.map(getIdFn)));
  }, []);

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const toggleItem = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback((items: T[], getIdFn: (item: T) => number) => {
    const allIds = items.map(getIdFn);
    const allSelected = allIds.every((id) => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allIds));
    }
  }, [selectedIds]);

  const isSelected = useCallback((id: number) => selectedIds.has(id), [selectedIds]);

  const selectedCount = selectedIds.size;

  const isAllSelected = useMemo(() => selectedIds.size > 0, [selectedIds]);

  const isSomeSelected = useMemo(() => selectedIds.size > 0, [selectedIds]);

  const getSelectedItems = useCallback(
    (items: T[], getIdFn: (item: T) => number) =>
      items.filter((item) => selectedIds.has(getIdFn(item))),
    [selectedIds]
  );

  return {
    selectedIds,
    isAllSelected,
    isSomeSelected,
    selectAll,
    deselectAll,
    toggleItem,
    toggleAll,
    isSelected,
    selectedCount,
    getSelectedItems,
  };
}
